import React, { useCallback, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { chunk } from 'lodash'

import { useUserSignedIn } from '@sketch/modules-common'

import {
  PublicationReactionsFragment,
  useAddReactionToPublicationMutation,
  usePublicationReactionsUpdatedSubscription,
} from '@sketch/gql-types'

import {
  DetailButton,
  HeartCount,
  DetailHeartIcon,
  ListButton,
  ListButtonIcon,
} from './HeartButton.styles'

const START_ANIMATION_DELAY = 1000

type HeartButtonFlavour = 'detail' | 'list'

export interface HeartButtonProps {
  className?: string
  publication: PublicationReactionsFragment
  trackHeartCount?: boolean
  flavour?: HeartButtonFlavour
}

interface DetailHeartButtonProps extends HeartButtonProps {
  mutate: () => void
  disabled?: boolean
}

const UNITS_BY_NUMBER = 3

const getUnitByLength = (length: number) => {
  switch (length) {
    case 3:
      return 'm'

    case 2:
      return 'k'

    default:
      return ''
  }
}

const formatHeartNumber = (totalHearts: number) => {
  const totalHeartsString = `${totalHearts}`
  const missingChunks = Math.abs(
    UNITS_BY_NUMBER - (totalHeartsString.length % UNITS_BY_NUMBER || 3)
  )

  const totalHeartsByUnit = chunk(
    [...Array.from(Array(missingChunks)), ...totalHeartsString],
    UNITS_BY_NUMBER
  )

  const integerPart = totalHeartsByUnit[0]?.join('')
  const decimalPart = totalHeartsByUnit[1]?.join('')
  const unit = getUnitByLength(totalHeartsByUnit.length)

  if (decimalPart) {
    return `${integerPart}.${decimalPart}`.slice(0, 4) + unit
  }

  return `${integerPart}${unit}`
}

const DetailHeartButton = React.forwardRef(function DetailHeartButton(
  props: DetailHeartButtonProps,
  ref
) {
  const { mutate, publication, className, disabled } = props
  const { totalHearts, userReaction } = publication

  const [animate, setAnimate] = useState(false)
  const timeout = useRef<NodeJS.Timeout | null>(null)
  const userHasHearted = userReaction === 'HEART'

  /**
   * This function is in-charge of cleaning the
   * animation tracks. Making sure that it's totally reset
   */
  const clearTimeOut = useCallback(() => {
    if (!timeout.current) {
      return
    }

    setAnimate(false)
    clearTimeout(timeout.current)
    timeout.current = null
  }, [])

  const handleOnClick = useCallback(() => {
    mutate()
    clearTimeOut()
  }, [clearTimeOut, mutate])

  const handleOnPointerDown = useCallback(() => {
    timeout.current = setTimeout(() => {
      setAnimate(true)
    }, START_ANIMATION_DELAY)
  }, [])

  return (
    <DetailButton
      ref={ref}
      disabled={!!disabled}
      className={className}
      buttonStyle="tertiary-40"
      onClick={handleOnClick}
      onPointerLeave={clearTimeOut}
      onPointerDown={!userHasHearted ? handleOnPointerDown : undefined}
      $fill={userHasHearted}
    >
      <DetailHeartIcon $animate={animate} />
      {totalHearts > 0 && (
        <HeartCount>{formatHeartNumber(totalHearts)}</HeartCount>
      )}
    </DetailButton>
  )
})

const HeartButton = (props: HeartButtonProps) => {
  const { className, publication, trackHeartCount, flavour } = props
  const { identifier, userReaction, totalHearts } = publication
  const userHasHearted = userReaction === 'HEART'
  const isUserSignedIn = useUserSignedIn()

  const [mutate] = useAddReactionToPublicationMutation({
    onError: 'show-toast',
    variables: {
      publicationIdentifier: identifier,
      reaction: userHasHearted ? 'NO_REACTION' : 'HEART',
    },
    optimisticResponse: {
      __typename: 'RootMutationType',
      addReactionToPublication: {
        __typename: 'AddReactionToPublicationResponse',
        publication: {
          __typename: 'Publication',
          identifier,
          totalHearts: totalHearts + (userHasHearted ? -1 : +1),
          userReaction: userHasHearted ? 'NO_REACTION' : 'HEART',
        },
      },
    },
  })

  /**
   * Subscribe to the Heart updates when the button
   * is in view, preventing unwanted listeners to be mounted
   */
  const [ref, inView] = useInView({
    skip: !trackHeartCount,
    rootMargin: '50px 0px 50px 0px',
  })

  usePublicationReactionsUpdatedSubscription({
    variables: { publicationIdentifier: publication.identifier },
    skip: !(trackHeartCount && inView),
  })

  if (flavour === 'list') {
    return (
      <ListButton
        className={className}
        ref={ref}
        disabled={!isUserSignedIn}
        $fill={userHasHearted}
        onClick={event => {
          /**
           * We are preventing the element from executing the link default action if the
           * the value is wrapped by one
           */
          event.preventDefault()

          mutate()
        }}
      >
        {totalHearts > 0 && formatHeartNumber(totalHearts)}
        <ListButtonIcon />
      </ListButton>
    )
  }

  return (
    <DetailHeartButton
      className={className}
      ref={ref}
      publication={publication}
      mutate={mutate}
      disabled={!isUserSignedIn}
    />
  )
}

export default HeartButton
